import { FormHandle } from '@/hooks/use-form-handle.hook';
import { FC } from 'react';
import InputControl from '@/components/form/input-control';
import { isWriteMode, ViewMode } from '@/lib/helpers';
import { Box, Stack } from '@mui/material';
import ExternalLink from '@/components/external-link';
import ConnectorStepCard from './connector-step-card';
import { AwsConnectorModel } from '@/lib/models/connector/aws-connector.model';
import { ConnectorModel } from '@/lib/models/connector/connector.model';
import ConnectorStepHeader from './connector-step-header';

const AwsAuthTypeOptions = [
  { label: 'IAM Role Assumption', value: 'role-assumption' },
  { label: 'Access Keys', value: 'access-keys' },
];

interface Props {
  connector: AwsConnectorModel;
  formHandle: FormHandle<ConnectorModel>;
  mode: ViewMode;
}

const AwsConnector: FC<Props> = ({ formHandle, connector, mode }) => {
  const { id, stackLink, status } = connector;
  const isNew = !id;
  const inWriteMode = isWriteMode(mode);
  const stackComplete = status === 'active';
  const { setFieldValue, values } = formHandle;

  const authType = (values as AwsConnectorModel).config?.authType || 'role-assumption';
  const isRoleAssumption = authType === 'role-assumption';

  const handleChangeAccountId = (value: string) => {
    if (!isNew || !value) {
      return;
    }

    setFieldValue('config.crossAccountRoleARN', AwsConnectorModel.getDefaultRoleARN(value));
  };

  return (
    <>
      {inWriteMode && (
        <Box>
          <InputControl
            name="config.authType"
            label="Authentication Type"
            formHandle={formHandle}
            type="select"
            options={AwsAuthTypeOptions}
            disabled={!isNew}
          />

          {isRoleAssumption && (
            <>
              <InputControl
                name="config.awsAccountId"
                label="AWS Account ID"
                formHandle={formHandle}
                onChange={handleChangeAccountId}
              />

              <InputControl
                name="config.awsRegion"
                label="AWS Region"
                formHandle={formHandle}
                type="aws-region-select"
              />

              <InputControl
                name="config.crossAccountRoleARN"
                label="Cross Account Role ARN"
                formHandle={formHandle}
                readonly={isNew}
              />
            </>
          )}

          {!isRoleAssumption && (
            <>
              <InputControl
                name="config.awsRegion"
                label="AWS Region"
                formHandle={formHandle}
                type="aws-region-select"
                withGovCloud
              />

              <InputControl
                name="config.accessKeyId"
                label="Access Key ID"
                formHandle={formHandle}
                required
              />

              <InputControl
                name="config.secretAccessKey"
                label="Secret Access Key"
                formHandle={formHandle}
                updateOnly={!isNew}
                required={isNew}
                type="password"
              />
            </>
          )}
        </Box>
      )}

      {!inWriteMode && isRoleAssumption && (
        <Box mt={2}>
          <ConnectorStepHeader title="Required External Setup" />
          <ConnectorStepCard title="Configure CloudFormation Stack" completed={stackComplete}>
            Please configure your AWS environment using the AWS CloudFormation stack below. Once
            complete, return to this page and verify the Cross-Account Role ARN of the role created
            by the stack.
            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
              <Box>
                <ExternalLink href={stackLink} label="Launch a CloudFormation Stack" />
              </Box>
              <Box>
                <ExternalLink
                  href="https://docs.aws.amazon.com/IAM/latest/UserGuide/id_roles_use_passrole.html"
                  label="Learn More"
                />
              </Box>
            </Stack>
          </ConnectorStepCard>
        </Box>
      )}
    </>
  );
};

export default AwsConnector;
